import React, { useState, useEffect, useCallback } from 'react';
import './PayoutsPage.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY     = process.env.REACT_APP_BACKEND_API_KEY || '';
const HEADERS     = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

async function apiFetch(path, opts = {}) {
  const res  = await fetch(`${BACKEND_URL}${path}`, { headers: HEADERS, ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

const STATUS_META = {
  pending:   { label: 'Pending',   color: '#f59e0b', bg: '#fffbeb' },
  processed: { label: 'Processed', color: '#039855', bg: '#ecfdf5' },
  rejected:  { label: 'Rejected',  color: '#ef4444', bg: '#fef2f2' },
};

const fmtTZS = (n) => {
  if (!n && n !== 0) return '—';
  const num = Number(n);
  if (num >= 1_000_000) return `TZS ${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000)     return `TZS ${(num / 1_000).toFixed(0)}K`;
  return `TZS ${num.toLocaleString()}`;
};

const PayoutsPage = () => {
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [acting, setActing]       = useState({});
  const [statusFilter, setStatus] = useState('pending');
  const [toast, setToast]         = useState(null);
  const [noteModal, setNoteModal] = useState(null); // {id, action}
  const [note, setNote]           = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await apiFetch(`/api/admin/payout-requests?status=${statusFilter}`);
      setRequests(data.requests || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setActing((a) => ({ ...a, [id]: true }));
    try {
      const path = `/api/admin/payout-requests/${id}/${action}`;
      await apiFetch(path, {
        method: 'POST',
        body: JSON.stringify({ note }),
      });
      setRequests((prev) => prev.filter((r) => r.$id !== id && r.requestId !== id));
      showToast(`Payout ${action === 'approve' ? 'approved' : 'rejected'} successfully.`);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setActing((a) => ({ ...a, [id]: false }));
      setNoteModal(null);
      setNote('');
    }
  };

  const openNote = (id, action) => { setNoteModal({ id, action }); setNote(''); };

  const counts = {
    total:     requests.length,
    amount:    requests.reduce((s, r) => s + Number(r.amount || 0), 0),
  };

  return (
    <div className="po-page">
      {toast && <div className={`po-toast po-toast--${toast.type}`}>{toast.msg}</div>}

      {noteModal && (
        <div className="po-overlay">
          <div className="po-modal">
            <h3 className="po-modal-title">
              {noteModal.action === 'approve' ? 'Approve Payout' : 'Reject Payout'}
            </h3>
            <p className="po-modal-sub">
              {noteModal.action === 'approve'
                ? 'Confirm you have sent the payment manually.'
                : 'State the reason for rejection (optional).'}
            </p>
            <textarea
              className="po-note-input"
              placeholder="Note (optional)…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
            <div className="po-modal-actions">
              <button className="po-btn po-btn--ghost" onClick={() => setNoteModal(null)}>
                Cancel
              </button>
              <button
                className={`po-btn ${noteModal.action === 'approve' ? 'po-btn--success' : 'po-btn--danger'}`}
                onClick={() => act(noteModal.id, noteModal.action)}
                disabled={acting[noteModal.id]}
              >
                {acting[noteModal.id] ? '…' : noteModal.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="po-header">
        <div>
          <h1 className="dashboard-title">Payout Requests</h1>
          <p className="dashboard-subtitle">Review and process seller & driver withdrawal requests.</p>
        </div>
        <button className="po-refresh" onClick={load} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div className="po-toolbar">
        {['pending', 'processed', 'rejected'].map((s) => (
          <button
            key={s}
            className={`po-tab ${statusFilter === s ? 'active' : ''}`}
            onClick={() => setStatus(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <span className="po-toolbar-sep" />
        <span className="po-summary">
          {counts.total} request{counts.total !== 1 ? 's' : ''} · {fmtTZS(counts.amount)} total
        </span>
      </div>

      {error && (
        <div className="po-error">{error} <button onClick={load}>Retry</button></div>
      )}

      {!loading && !error && requests.length === 0 && (
        <div className="po-empty">No {statusFilter} payout requests.</div>
      )}

      {!loading && requests.length > 0 && (
        <div className="po-table-wrap">
          <table className="po-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Phone</th>
                <th>Requested</th>
                <th>Status</th>
                {statusFilter === 'pending' && <th>Actions</th>}
                {statusFilter !== 'pending' && <th>Note</th>}
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => {
                const id     = r.$id || r.requestId;
                const sm     = STATUS_META[r.status] || STATUS_META.pending;
                const busy   = acting[id];
                const date   = r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB') : '—';
                return (
                  <tr key={id}>
                    <td><code className="po-code">{(r.userId || '').slice(0, 12)}…</code></td>
                    <td><span className="po-type">{r.userType || '—'}</span></td>
                    <td className="po-amount">{fmtTZS(r.amount)}</td>
                    <td>{r.phone || '—'}</td>
                    <td>{date}</td>
                    <td>
                      <span className="po-chip" style={{ color: sm.color, background: sm.bg }}>
                        {sm.label}
                      </span>
                    </td>
                    {statusFilter === 'pending' && (
                      <td className="po-actions">
                        <button
                          className="po-btn po-btn--success po-btn--sm"
                          onClick={() => openNote(id, 'approve')}
                          disabled={busy}
                        >
                          {busy ? '…' : 'Approve'}
                        </button>
                        <button
                          className="po-btn po-btn--danger po-btn--sm"
                          onClick={() => openNote(id, 'reject')}
                          disabled={busy}
                        >
                          Reject
                        </button>
                      </td>
                    )}
                    {statusFilter !== 'pending' && (
                      <td className="po-note-cell">{r.note || '—'}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {loading && (
        <div className="po-loading"><div className="mp-spinner" /> Loading…</div>
      )}
    </div>
  );
};

export default PayoutsPage;
